import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  TextField,
  IconButton,
  Link,
  Chip,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';

// Shared look (design tokens) - same as the rest of IZHub
const ui = {
  primary: '#009db9',
  primaryDark: '#007a8a',
  primarySoft: '#e6f6f9',
  text: '#1f2933',
  muted: '#6b7280',
  border: '#e5e7eb',
  surface: '#f7f9fb',
  danger: '#d32f2f',
  dangerSoft: '#ffebee',
  shadow: '0 4px 20px rgba(16, 24, 40, 0.06)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#fff',
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ui.primary },
  },
  '& label.Mui-focused': { color: ui.primary },
};

const primaryButtonSx = {
  bgcolor: ui.primary,
  color: '#fff',
  textTransform: 'none',
  fontWeight: 700,
  borderRadius: '10px',
  px: 3,
  py: 1.1,
  boxShadow: 'none',
  '&:hover': { bgcolor: ui.primaryDark, boxShadow: 'none' },
};

const dialogPaperSx = {
  borderRadius: '16px',
  m: { xs: 2, sm: 4 },
  width: '100%',
};

const DialogHeader = ({ title, onClose }) => (
  <DialogTitle
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      fontWeight: 700,
      color: ui.text,
      pb: 1.5,
    }}
  >
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: '10px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: ui.primarySoft,
        color: ui.primary,
      }}
    >
      <SchoolOutlinedIcon fontSize="small" />
    </Box>
    <Box sx={{ flexGrow: 1 }}>{title}</Box>
    <IconButton onClick={onClose} size="small" aria-label="Close" sx={{ color: ui.muted }}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </DialogTitle>
);

const Diploma = ({ diplomas, setDiplomas, onDeleteDiploma = () => {} }) => {
  const [showDiplomaModal, setShowDiplomaModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDiploma, setEditingDiploma] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);

  const initialDiplomaState = {
    name: '',
    organization: '',
    issueDate: null,
    url: '',
    comments: '',
    mediaFilePath: '',
  };

  const [newDiploma, setNewDiploma] = useState(initialDiplomaState);

  // Resets the form to initial state
  const resetForm = () => {
    setNewDiploma(initialDiplomaState);
  };

  // Opens edit modal with diploma data
  const handleEditDiploma = (index) => {
    setEditingIndex(index);
    setEditingDiploma({
      ...diplomas[index],
      issueDate: diplomas[index].issueDate ? dayjs(diplomas[index].issueDate) : null,
    });
    setShowEditModal(true);
  };

  // Saves edited diploma
  const handleSaveEdit = () => {
    const isIssueDateValid = editingDiploma?.issueDate && dayjs(editingDiploma.issueDate).isValid();
    if (!editingDiploma?.name || !editingDiploma?.organization || !isIssueDateValid) {
      alert('Please fill in all required fields: Name, Organization, and Issue Date.');
      return;
    }

    const updatedDiploma = {
      ...editingDiploma,
      issueDate: editingDiploma.issueDate ? dayjs(editingDiploma.issueDate).format('YYYY-MM-DD') : null,
    };

    setDiplomas({
      action: 'update',
      diploma: updatedDiploma,
      index: editingIndex,
    });

    setShowEditModal(false);
    setEditingDiploma(null);
    setEditingIndex(null);
  };

  // Deletes a diploma by ID
  const handleDeleteDiploma = (diplomaId) => {
    if (typeof onDeleteDiploma === 'function') {
      onDeleteDiploma(diplomaId);
    } else {
      alert('Delete functionality is not available at this time.');
    }
  };

  // Adds a new diploma
  const handleAddDiploma = () => {
    const isIssueDateValid = newDiploma.issueDate && dayjs(newDiploma.issueDate).isValid();
    if (!newDiploma.name || !newDiploma.organization || !isIssueDateValid) {
      alert('Please fill in all required fields: Name, Organization, and Issue Date.');
      return;
    }

    const formattedDiploma = {
      id: crypto.randomUUID(),
      name: newDiploma.name,
      organization: newDiploma.organization,
      issueDate: newDiploma.issueDate ? dayjs(newDiploma.issueDate).format('YYYY-MM-DD') : null,
      url: newDiploma.url || '',
      comments: newDiploma.comments || '',
      mediaFilePath: newDiploma.mediaFilePath || '',
    };

    setDiplomas({
      action: 'add',
      diploma: formattedDiploma,
    });

    resetForm();
    setShowDiplomaModal(false);
  };

  // Closes the add diploma modal and resets form
  const handleCloseModal = () => {
    setShowDiplomaModal(false);
    resetForm();
  };

  const safeDiplomas = Array.isArray(diplomas) ? diplomas : [];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box
        sx={{
          mb: 3,
          borderRadius: '16px',
          border: `1px solid ${ui.border}`,
          backgroundColor: '#fff',
          boxShadow: ui.shadow,
          p: { xs: 2.5, sm: 3.5 },
        }}
      >
        {/* Section header */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: ui.primarySoft,
                color: ui.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SchoolOutlinedIcon fontSize="small" />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              <Link
                href="/diploma"
                sx={{
                  color: ui.text,
                  '&:hover': { color: ui.primary },
                  textDecoration: 'none',
                }}
              >
                Diplomas
              </Link>
            </Typography>
            <Chip
              size="small"
              label={safeDiplomas.length}
              sx={{ fontWeight: 700, borderRadius: '8px', backgroundColor: ui.primarySoft, color: ui.primaryDark }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowDiplomaModal(true)}
            sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
          >
            Add Diploma
          </Button>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {safeDiplomas.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 5,
                px: 2,
                borderRadius: '12px',
                border: `1px dashed ${ui.border}`,
                backgroundColor: ui.surface,
              }}
            >
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  mx: 'auto',
                  mb: 1.5,
                  borderRadius: '50%',
                  backgroundColor: ui.primarySoft,
                  color: ui.primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <SchoolOutlinedIcon />
              </Box>
              <Typography variant="body1" sx={{ color: ui.muted }}>
                No diplomas added yet.
              </Typography>
            </Box>
          ) : (
            safeDiplomas.map((diploma, index) => {
              const issueDateFormatted = diploma.issueDate && dayjs(diploma.issueDate).isValid()
                ? dayjs(diploma.issueDate).format('MM/YYYY')
                : 'N/A';

              return (
                <Box
                  key={diploma.id || index}
                  sx={{
                    p: { xs: 2, sm: 2.5 },
                    borderRadius: '14px',
                    border: `1px solid ${ui.border}`,
                    borderLeft: `5px solid ${ui.primary}`,
                    backgroundColor: '#fff',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 2,
                    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(16, 24, 40, 0.08)',
                      transform: 'translateY(-1px)',
                    },
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 700, color: ui.text, fontSize: '1.05rem', wordBreak: 'break-word' }}>
                      {diploma.name || 'Unnamed Diploma'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: ui.muted, wordBreak: 'break-word' }}>
                      {diploma.organization || 'Unknown Organization'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      <Chip
                        size="small"
                        icon={<EventOutlinedIcon sx={{ fontSize: '1rem !important' }} />}
                        label={`Issued: ${issueDateFormatted}`}
                        sx={{ fontWeight: 600, borderRadius: '8px', backgroundColor: ui.surface, border: `1px solid ${ui.border}` }}
                      />
                    </Box>
                    {diploma.url && (
                      <Link
                        href={diploma.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          mt: 1.25,
                          fontWeight: 600,
                          color: ui.primary,
                          '&:hover': { color: ui.primaryDark },
                          textDecoration: 'none',
                        }}
                      >
                        View Diploma
                        <OpenInNewIcon sx={{ fontSize: '1rem' }} />
                      </Link>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      gap: 1,
                      flexShrink: 0,
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<EditOutlinedIcon fontSize="small" />}
                      onClick={() => handleEditDiploma(index)}
                      sx={{
                        flex: { xs: 1, sm: 'none' },
                        color: ui.primary,
                        borderColor: ui.primary,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '10px',
                        '&:hover': { borderColor: ui.primaryDark, backgroundColor: ui.primarySoft },
                      }}
                    >
                      Edit
                    </Button>
                    <Tooltip title="Delete">
                      <Button
                        variant="outlined"
                        aria-label="Delete diploma"
                        onClick={() => handleDeleteDiploma(diploma.id)}
                        sx={{
                          minWidth: 44,
                          px: 1.25,
                          color: ui.danger,
                          borderColor: '#f5c2c7',
                          borderRadius: '10px',
                          '&:hover': { borderColor: ui.danger, backgroundColor: ui.dangerSoft },
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </Box>

      {/* Add diploma dialog */}
      <Dialog
        open={showDiplomaModal}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogHeader title="Add Diploma" onClose={handleCloseModal} />
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2.5,
              mt: 0.5,
            }}
          >
            <TextField
              id="name"
              label="Name"
              fullWidth
              value={newDiploma.name}
              onChange={(e) => setNewDiploma({ ...newDiploma, name: e.target.value })}
              variant="outlined"
              required
              sx={fieldSx}
            />
            <TextField
              id="organization"
              label="Organization"
              fullWidth
              value={newDiploma.organization}
              onChange={(e) => setNewDiploma({ ...newDiploma, organization: e.target.value })}
              variant="outlined"
              required
              sx={fieldSx}
            />
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <DatePicker
                label="Issue Date"
                views={['year', 'month', 'day']}
                format="YYYY-MM-DD"
                value={newDiploma.issueDate}
                onChange={(newValue) => setNewDiploma({ ...newDiploma, issueDate: newValue ? dayjs(newValue) : null })}
                slotProps={{ textField: { fullWidth: true, required: true, sx: fieldSx } }}
              />
            </Box>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <TextField
                label="URL (Optional)"
                fullWidth
                type="url"
                value={newDiploma.url}
                onChange={(e) => setNewDiploma({ ...newDiploma, url: e.target.value })}
                placeholder="https://example.com/diploma"
                variant="outlined"
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <TextField
                label="Comments (Optional)"
                multiline
                minRows={3}
                fullWidth
                value={newDiploma.comments}
                onChange={(e) => setNewDiploma({ ...newDiploma, comments: e.target.value })}
                inputProps={{ maxLength: 500 }}
                helperText={`${newDiploma.comments?.length || 0}/500 characters`}
                sx={fieldSx}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={handleCloseModal}
            sx={{ color: ui.muted, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAddDiploma} sx={primaryButtonSx}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit diploma dialog */}
      <Dialog
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogHeader title="Edit Diploma" onClose={() => setShowEditModal(false)} />
        <Divider />
        <DialogContent sx={{ pt: 2.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 2.5,
              mt: 0.5,
            }}
          >
            <TextField
              id="edit-name"
              label="Name"
              fullWidth
              value={editingDiploma?.name || ''}
              onChange={(e) => setEditingDiploma({ ...editingDiploma, name: e.target.value })}
              variant="outlined"
              required
              sx={fieldSx}
            />
            <TextField
              id="edit-organization"
              label="Organization"
              fullWidth
              value={editingDiploma?.organization || ''}
              onChange={(e) => setEditingDiploma({ ...editingDiploma, organization: e.target.value })}
              variant="outlined"
              required
              sx={fieldSx}
            />
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <DatePicker
                label="Issue Date"
                views={['year', 'month', 'day']}
                format="YYYY-MM-DD"
                value={editingDiploma?.issueDate || null}
                onChange={(newValue) => setEditingDiploma({ ...editingDiploma, issueDate: newValue ? dayjs(newValue) : null })}
                slotProps={{ textField: { fullWidth: true, required: true, sx: fieldSx } }}
              />
            </Box>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <TextField
                id="edit-url"
                label="URL (Optional)"
                fullWidth
                type="url"
                value={editingDiploma?.url || ''}
                onChange={(e) => setEditingDiploma({ ...editingDiploma, url: e.target.value })}
                placeholder="https://example.com/diploma"
                variant="outlined"
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ gridColumn: { sm: '1 / -1' } }}>
              <TextField
                label="Comments (Optional)"
                multiline
                minRows={3}
                fullWidth
                value={editingDiploma?.comments || ''}
                onChange={(e) => setEditingDiploma({ ...editingDiploma, comments: e.target.value })}
                inputProps={{ maxLength: 500 }}
                helperText={`${editingDiploma?.comments?.length || 0}/500 characters`}
                sx={fieldSx}
              />
            </Box>
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button
            onClick={() => setShowEditModal(false)}
            sx={{ color: ui.muted, textTransform: 'none', fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveEdit} sx={primaryButtonSx}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default Diploma;